import { type ParsedUrlQuery } from 'querystring';
import { persist } from 'zustand/middleware';
import { create } from 'zustand';
import {
  defaultPaginationIndex,
  defaultPaginationSize,
  pageSizeOptions,
  searchPageSizeOptions,
} from '@/types/pagination';
import { defaultColumnsSettings, defaultSortSettings } from '@/types/datatable';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { type Invitation } from '@/types/invitations';
import { type MetadataField } from '@/types/metadata';
import { type EntityType } from '@/types/entity';
import { type User } from '@/types/users';
import { type Tag } from '@/types/tags';

export type PaginationState = {
  pageSize: number;
  pageIndex: number;
  sortOrder: SortOrderOptions;
  sortValue: SortOrderValues;
};

const DEFAULT_PAGINATION: PaginationState = {
  pageSize: defaultPaginationSize,
  pageIndex: defaultPaginationIndex,
  sortOrder: SortOrderOptions.ASC,
  sortValue: SortOrderValues.CREATED_AT,
};

const defaultTableSettings = (tableEntity: EntityType): TableSettings => ({
  columnVisibility: {},
  columnOrder: [],
  ...DEFAULT_PAGINATION,
  sortValue: defaultSortSettings[tableEntity],
});

// Search mode offers its own page sizes (250), so both option sets are valid store values.
const allowedPageSizes = [...pageSizeOptions, ...searchPageSizeOptions];

// Guards untrusted pagination/sort values from URL params or corrupted localStorage
// against the allowed constants, falling back to per-entity defaults on any mismatch.
const sanitizePagination = <T extends Partial<PaginationState>>(tableEntity: EntityType, settings: T): T => ({
  ...settings,
  pageSize: allowedPageSizes.includes(settings.pageSize as number) ? settings.pageSize : defaultPaginationSize,
  pageIndex:
    Number.isInteger(settings.pageIndex) && (settings.pageIndex as number) >= 0
      ? settings.pageIndex
      : defaultPaginationIndex,
  sortOrder: Object.values(SortOrderOptions).includes(settings.sortOrder as SortOrderOptions)
    ? settings.sortOrder
    : SortOrderOptions.ASC,
  sortValue: Object.values(SortOrderValues).includes(settings.sortValue as SortOrderValues)
    ? settings.sortValue
    : defaultSortSettings[tableEntity],
});

export type RowEntity = {
  id: string;
  getValue: (key: string) => any;
  original: User | Invitation | Tag | MetadataField;
};

export interface TableSettings {
  columnVisibility: Record<string, boolean>;
  columnOrder: Array<string>;
  pageSize: number;
  pageIndex: number;
  sortOrder: SortOrderOptions;
  sortValue: SortOrderValues;
  // add filters, sortState, etc.
}

interface TableStore {
  // Persisted settings
  settings: Partial<Record<EntityType, TableSettings>>;
  setSettings: (tableEntity: EntityType, updates: Partial<TableSettings>) => void;
  resetSettings: (tableEntity: EntityType) => void;
  getTableSettings: (tableEntity: EntityType) => TableSettings;

  getPagination: (tableEntity: EntityType) => PaginationState;
  setPagination: (tableEntity: EntityType, updates: Partial<PaginationState>) => void;

  setPageSize: (tableEntity: EntityType, pageSize: number) => void;
  setPageIndex: (tableEntity: EntityType, pageIndex: number) => void;
  setSortOrder: (tableEntity: EntityType, sortOrder: SortOrderOptions) => void;
  setSortValue: (tableEntity: EntityType, sortValue: SortOrderValues) => void;

  // Hydration from query
  hydratePaginationFromQuery: (tableEntity: EntityType, query: ParsedUrlQuery) => void;

  // Non-persisted
  // Per-view list filter (the "Filter results in this view" text). Shared via the store
  // so any component can read/clear it without prop-drilling; intentionally not persisted.
  queryStrings: Partial<Record<EntityType, string>>;
  setQueryString: (tableEntity: EntityType, queryString: string) => void;
  // Filter visibility lives here too: useSearchQuery runs in more than one component for the
  // same entity (home + gallery layout), and closing the filter from one must close them all.
  showFilterInputs: Partial<Record<EntityType, boolean>>;
  setShowFilterInput: (tableEntity: EntityType, showFilterInput: boolean) => void;
  selectedEntity: RowEntity | null;
  setSelectedEntity: (entity: RowEntity | null) => void;
  selectedEntityType: EntityType | null;
  setSelectedEntityType: (type: EntityType | null) => void;
}

export const useTableStore = create<TableStore>()(
  persist(
    (set, get) => {
      // Track which tables have already been hydrated from query
      const hydratedTables = new Set<EntityType>();

      return {
        // Persisted
        settings: {},

        setSettings: (tableEntity, updates) =>
          set((state) => {
            const prev = state.settings[tableEntity] ?? defaultTableSettings(tableEntity);
            const next: TableSettings = { ...prev, ...updates };
            return { settings: { ...state.settings, [tableEntity]: next } };
          }),

        resetSettings: (tableEntity) =>
          set((state) => {
            const { [tableEntity]: _, ...rest } = state.settings;
            hydratedTables.delete(tableEntity);
            return { settings: rest };
          }),

        getTableSettings: (tableEntity) => {
          const state = get();
          return sanitizePagination(tableEntity, {
            ...defaultTableSettings(tableEntity),
            ...state.settings[tableEntity],
          });
        },

        getPagination: (tableEntity) => {
          const s = get().settings[tableEntity];

          return s
            ? sanitizePagination(tableEntity, {
                pageSize: s.pageSize,
                pageIndex: s.pageIndex,
                sortOrder: s.sortOrder,
                sortValue: s.sortValue,
              })
            : { ...DEFAULT_PAGINATION, sortValue: defaultSortSettings[tableEntity] };
        },

        setPagination: (tableEntity, updates) =>
          set((state) => {
            const prev = state.settings[tableEntity] ?? defaultTableSettings(tableEntity);
            const next: TableSettings = {
              ...prev,
              ...updates,
            };
            return { settings: { ...state.settings, [tableEntity]: next } };
          }),

        setPageSize: (tableEntity, pageSize) => get().setPagination(tableEntity, { pageSize }),
        setPageIndex: (tableEntity, pageIndex) => get().setPagination(tableEntity, { pageIndex }),
        setSortOrder: (tableEntity, sortOrder) => get().setPagination(tableEntity, { sortOrder }),
        setSortValue: (tableEntity, sortValue) => get().setPagination(tableEntity, { sortValue }),

        // Hydrate pagination from query (only once per table)
        hydratePaginationFromQuery: (tableEntity, query) => {
          if (hydratedTables.has(tableEntity)) {
            return;
          }

          const pageSize = query.pageSize ? Number(query.pageSize) : undefined;
          const pageIndex = query.pageIndex ? Math.max(0, Number(query.pageIndex) - 1) : undefined;
          const sortOrder = query.sortOrder ? (String(query.sortOrder) as SortOrderOptions) : SortOrderOptions.ASC;
          const sortValue = query.sortValue
            ? (String(query.sortValue) as SortOrderValues)
            : defaultSortSettings[tableEntity];

          if (pageSize !== undefined || pageIndex !== undefined) {
            const sanitized = sanitizePagination(tableEntity, {
              pageSize: pageSize ?? DEFAULT_PAGINATION.pageSize,
              pageIndex: pageIndex ?? DEFAULT_PAGINATION.pageIndex,
              sortOrder,
              sortValue,
            });
            /**
             * Read-site `?? defaultColumnsSettings[tableEntity]` fallbacks only trigger while
             * settings[tableEntity] is absent, so seed real column defaults here first if there's
             * no persisted column state yet - otherwise this write permanently sticks it with an
             * empty {}/[] shape.
             */
            const existing = get().settings[tableEntity];
            const hasColumnSettings =
              Boolean(existing) &&
              (existing!.columnOrder.length > 0 || Object.keys(existing!.columnVisibility).length > 0);
            if (!hasColumnSettings) {
              get().setSettings(tableEntity, defaultColumnsSettings[tableEntity]);
            }
            get().setSettings(tableEntity, sanitized);
            hydratedTables.add(tableEntity);
          }
        },

        // Non-persisted
        queryStrings: {},
        setQueryString: (tableEntity, queryString) =>
          set((state) => ({ queryStrings: { ...state.queryStrings, [tableEntity]: queryString } })),
        showFilterInputs: {},
        setShowFilterInput: (tableEntity, showFilterInput) =>
          set((state) => ({ showFilterInputs: { ...state.showFilterInputs, [tableEntity]: showFilterInput } })),
        selectedEntity: null,
        setSelectedEntity: (entity) => set({ selectedEntity: entity }),
        selectedEntityType: null,
        setSelectedEntityType: (type) => set({ selectedEntityType: type }),
      };
    },
    {
      name: 'app-table-settings',
      version: 2.4,
      partialize: (state) => ({ settings: state.settings }),
    },
  ),
);
